import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { createFileRoute, Link } from "@tanstack/react-router";

const faqs: { q: string; a: string }[] = [
  {
    q: "Who is Raj Shamani?",
    a: "Raj Shamani is an Indian entrepreneur, angel investor, content creator, and global speaker. He is the director of Shamani Industries, host of the Figuring Out podcast — one of India's most-watched business podcasts — and an active angel investor in 20+ Indian startups. He has spoken at the United Nations and 150+ stages across 25+ countries.",
  },
  {
    q: "How old is Raj Shamani and when was he born?",
    a: "Raj Shamani was born on 22 March 1996 in Indore, Madhya Pradesh, India. He is currently in his late twenties.",
  },
  {
    q: "Where is Raj Shamani from?",
    a: "Raj Shamani was born and raised in Indore, Madhya Pradesh, in a Marwari business family. His family runs Shamani Industries, a manufacturing company based in Indore.",
  },
  {
    q: "What is Raj Shamani's educational background?",
    a: "Raj studied at Daly College, Indore — one of India's oldest and most prestigious boarding schools. Much of his real-world business education came from working inside Shamani Industries from age 16.",
  },
  {
    q: "What is Raj Shamani's net worth?",
    a: "Raj Shamani has not officially disclosed his net worth. His income streams include Shamani Industries, the Figuring Out podcast and media company, brand partnerships, paid international speaking, and a portfolio of 20+ early-stage startup investments. Public estimates online are speculation.",
  },
  {
    q: "What companies does Raj Shamani run?",
    a: "Raj is a director at Shamani Industries (polymers and additives manufacturing), co-founder of House of X (a D2C venture studio), and founder of Figuring Out Media. He is also an active angel investor across D2C, fintech, and creator-economy startups.",
  },
  {
    q: "What is the Figuring Out podcast?",
    a: "Figuring Out is Raj Shamani's long-form interview podcast featuring India's top founders, investors, creators, and cultural voices. It is available on YouTube, Spotify, and Apple Podcasts and is one of the most-watched business podcasts in India.",
  },
  {
    q: "Where can I listen to the Figuring Out podcast?",
    a: "Figuring Out is available on YouTube (youtube.com/@RajShamani), Spotify, and Apple Podcasts. You can also read episode summaries, key takeaways, and full transcripts on the Podcast page of this site.",
  },
  {
    q: "How can I be a guest on Figuring Out?",
    a: "Fill out the pitch form on the Be a Guest page. Every application is reviewed by Raj's team. Guests are selected based on the strength of the story, unique lived experience, and fit with the show.",
  },
  {
    q: "How do I contact Raj Shamani for a brand or speaking engagement?",
    a: "For brand collaborations, speaking enquiries, and partnerships, use the Contact page or email partnerships@figuringout.co.",
  },
  {
    q: "Has Raj Shamani spoken at the United Nations?",
    a: "Yes. Raj spoke at the United Nations on youth entrepreneurship in 2017 and has since addressed 150+ stages across more than 25 countries.",
  },
  {
    q: "Did Raj Shamani write a book?",
    a: "Yes. Raj is the author of Build, Don't Talk — a book on personal branding, communication, and building a career or business by executing more than you announce. Details are on the Build, Don't Talk page.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "Raj Shamani FAQ — Age, Net Worth, Podcast & Business", key: "title" },
      { name: "description", content: "Answers to the most-asked questions about Raj Shamani — age, birthday, education, net worth, family, businesses, and the Figuring Out podcast.", key: "description" },
      { property: "og:title", content: "Raj Shamani FAQ — Age, Net Worth, Podcast & Business", key: "og:title" },
      { property: "og:description", content: "Answers to the most-asked questions about Raj Shamani — age, birthday, education, net worth, family, businesses, and the Figuring Out podcast.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/faq", key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: "Raj Shamani FAQ", key: "twitter:title" },
      { name: "twitter:description", content: "Answers to the most-asked questions about Raj Shamani.", key: "twitter:description" },
    ],
    links: [{ rel: "canonical", href: "https://rajshamani.life/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FAQ,
});

function FAQ() {
  return (
    <div>
      <PageBreadcrumb current="FAQ" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-4xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">FAQ</div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Frequently Asked <span className="text-gradient-gold">Questions</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            The most-asked questions about Raj Shamani — his age, background, businesses,
            the Figuring Out podcast, and how to get in touch.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-16">
        <div className="space-y-6">
          {faqs.map((f, i) => (
            <article key={i} className="p-6 rounded-xl bg-card border border-border">
              <h2 className="text-xl font-bold mb-3">{f.q}</h2>
              <p className="text-muted-foreground leading-relaxed">{f.a}</p>
            </article>
          ))}
        </div>

        <div className="mt-16 p-8 rounded-xl border border-gold/30 bg-gold/5 text-center">
          <h2 className="text-2xl font-bold mb-3">Still have a question?</h2>
          <p className="text-muted-foreground mb-6">
            Read the full <Link to="/about" className="text-gold underline">biography</Link>,
            browse the <Link to="/raj-shamani-podcast" className="text-gold underline">Raj Shamani podcast</Link> guide,
            see <Link to="/raj-shamani-books" className="text-gold underline">Raj Shamani books</Link>,
            or <Link to="/contact" className="text-gold underline">get in touch</Link>.
          </p>
        </div>
      </section>

      <KeywordHubLinks />

    </div>
  );
}
